import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { EventCard, type Event } from "./EventCard";

interface DayEventsModalProps {
  dateString: string | null;
  events: Event[];
  onClose: () => void;
  onEventClick: (event: Event) => void;
}

export function DayEventsModal({
  dateString,
  events,
  onClose,
  onEventClick,
}: DayEventsModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dateString) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    closeButtonRef.current?.focus();
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !modalRef.current) return;

      const focusable = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener("keydown", handleTab);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTab);
      document.body.style.overflow = "";
    };
  }, [dateString, onClose]);

  if (!dateString || events.length === 0) return null;

  const formattedDate = new Date(dateString + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md">
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="day-modal-title"
          className="bg-card rounded-xl border border-border/50 overflow-hidden h-full sm:h-auto max-h-[calc(100vh-2rem)] flex flex-col"
        >
          {/* Header */}
          <div className="relative p-5 pb-4 border-b border-border/50">
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>

            <h2 id="day-modal-title" className="font-display text-lg text-foreground pr-10">
              {formattedDate}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {events.length} event{events.length !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Events list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {events.map((event) => (
              <EventCard
                key={event._id}
                event={event}
                onClick={() => onEventClick(event)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
